import { createContext, useContext } from 'react';

/**
 * The colours the floor-plan canvas draws its own marks in — the floor, the
 * grid, the walls, labels, outlines, empty seats, the selection. Colours a
 * producer chose (a table's fill, a room feature's) are theirs and are not
 * here. The canvas is an SVG drawing, not page chrome, so it cannot follow the
 * app's theme through CSS classes: a dark page behind light-theme ink loses
 * the walls and makes the grid glare.
 */
export interface CanvasPalette {
  /** The floor behind everything. */
  background: string;
  gridMajor: string;
  gridMinor: string;
  /** Room walls and the plain outline. */
  wall: string;
  /** Inside a drawn room. */
  roomFloor: string;
  /** Room door and window marks. */
  opening: string;
  /** Names, numbers and seat counts. */
  label: string;
  labelMuted: string;
  labelFaint: string;
  /** An object's outline when it has no colour of its own. */
  outline: string;
  /** A seat with nobody in it, and its edge. */
  seatEmpty: string;
  seatEmptyStroke: string;
  /** Fixed grey parts: stairs, doors, windows. */
  fixture: string;
  selection: string;
  /** Legends drawn on the canvas. */
  panel: string;
  panelBorder: string;
  /** A room feature's name and edge, over its see-through fill. */
  featureInk: string;
  featureEdge: string;
}

export type CanvasTheme = 'light' | 'dark';

/** How the canvas is drawn: like the app, or always light or dark. */
export type CanvasThemeChoice = 'app' | CanvasTheme;

export const CANVAS_THEME_CHOICES: readonly CanvasThemeChoice[] = ['app', 'light', 'dark'];

export function isCanvasThemeChoice(value: unknown): value is CanvasThemeChoice {
  return value === 'app' || value === 'light' || value === 'dark';
}

export const CANVAS_PALETTES: Record<CanvasTheme, CanvasPalette> = {
  light: {
    background: '#f9fafb',
    gridMajor: '#d1d5db',
    gridMinor: '#e5e7eb',
    wall: '#374151',
    roomFloor: '#f9fafb',
    opening: '#6b7280',
    label: '#374151',
    labelMuted: '#6b7280',
    labelFaint: '#9ca3af',
    outline: '#6b7280',
    seatEmpty: '#e5e7eb',
    seatEmptyStroke: '#9ca3af',
    fixture: '#9ca3af',
    selection: '#2563eb',
    panel: '#ffffff',
    panelBorder: '#d1d5db',
    featureInk: '#44403c',
    featureEdge: '#57534e',
  },
  // A blueprint-dark floor: pale walls and labels, grid lines only a step
  // off the floor so they guide without competing with the furniture.
  dark: {
    background: '#111827',
    gridMajor: '#374151',
    gridMinor: '#1f2937',
    wall: '#e5e7eb',
    roomFloor: '#1a2230',
    opening: '#9ca3af',
    label: '#e5e7eb',
    labelMuted: '#9ca3af',
    labelFaint: '#6b7280',
    outline: '#9ca3af',
    seatEmpty: '#374151',
    seatEmptyStroke: '#6b7280',
    fixture: '#6b7280',
    selection: '#60a5fa',
    panel: '#1f2937',
    panelBorder: '#4b5563',
    featureInk: '#f5f5f4',
    featureEdge: '#d6d3d1',
  },
};

/**
 * A door's or window's own colour, drawn in this palette. The library gives
 * them the light floor as a fill (so the wall line under them is hidden) and
 * the light walls as a frame; stored with the item, those would draw a white
 * box with a dark frame on a dark floor. So a **fill** equal to the light
 * floor follows the floor, and a **frame** equal to the light wall follows the
 * walls — each only in its own role, so a fill chosen in the wall's colour, or
 * a frame in the floor's, is drawn as chosen. (A fill chosen as exactly the
 * light floor cannot be told from the library's, and means the same thing on
 * a door: match the floor.)
 */
export function fixtureColour(
  colour: string,
  role: 'fill' | 'frame',
  palette: CanvasPalette,
): string {
  const c = colour.toLowerCase();
  if (role === 'fill' && c === CANVAS_PALETTES.light.background) return palette.background;
  if (role === 'frame' && c === CANVAS_PALETTES.light.wall) return palette.wall;
  return colour;
}

/** The canvas theme a choice comes to, given the app's own. */
export function resolveCanvasTheme(choice: CanvasThemeChoice, appTheme: CanvasTheme): CanvasTheme {
  return choice === 'app' ? appTheme : choice;
}

/**
 * The palette the canvas under it draws with. Light by default, so a drawing
 * rendered outside the editor (the room editor, a test) is unchanged.
 */
export const CanvasPaletteContext = createContext<CanvasPalette>(CANVAS_PALETTES.light);

export function useCanvasPalette(): CanvasPalette {
  return useContext(CanvasPaletteContext);
}
